import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { clsx } from '@wego/alexis/helpers/clsx';
import { translateNumber } from '@wego/alexis/helpers/translation';

import Image from '@components/Image';

import {
  cloudflareImageTransformations,
  cloudinaryImageTransformations,
  isCloudinarySrc,
} from '@helpers/imageTransformations';

import ChevronIcon from '@icons/chevron_left.svg';
import CloseIcon from '@icons/close.svg';

import { getIsRtlState } from '@redux/selectors';

import styles from './ImageGallery.module.scss';

interface ImageGalleryProps {
  imageUrls: string[];
  startImageIndex: number;
  isOpen: boolean;
  locale: string;
  onClose: (event: React.MouseEvent | KeyboardEvent) => void;
}

// For smooth animations between a moving target
const MOUSE_EASE = 1.5;

const ImageGallery: React.FC<ImageGalleryProps> = ({
  imageUrls,
  startImageIndex,
  isOpen,
  locale,
  onClose,
}) => {
  const imagesContainerRef = useRef<HTMLDivElement>(null);
  const imageRef = useRef(Array(imageUrls.length).fill(null));
  const mouseCoords = useRef({
    startX: 0,
    startY: 0,
    scrollLeft: 0,
    scrollTop: 0,
  });

  const [selectedImageIndex, setSelectedImageIndex] = useState<number>(0);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const isRtl = useSelector(getIsRtlState);

  const noImagePlaceholderSrc = cloudinaryImageTransformations(
    'https://assets.wego.com/image/upload/v1639463186/web/misc/hotel-image-placeholder.png',
    'h_692',
    'c_fill',
    'f_auto',
    'fl_lossy',
    'q_auto:low',
    'g_auto',
  );

  const getImageTransformationsUrl = useCallback<(url: string, height: number) => string>(
    (url: string, height: number) => {
      if (isCloudinarySrc(url)) {
        return cloudinaryImageTransformations(
          url,
          `h_${height}`,
          'c_fill',
          'f_auto',
          'fl_lossy',
          'q_auto:best',
          'g_auto',
        );
      } else {
        return cloudflareImageTransformations(url, { format: 'auto', height, quality: 90 });
      }
    },
    [],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const isMovingBackward = event.key === (isRtl ? 'ArrowRight' : 'ArrowLeft');
      const isMovingForward = event.key === (isRtl ? 'ArrowLeft' : 'ArrowRight');

      if (isMovingBackward && selectedImageIndex > 0) {
        setSelectedImageIndex((prev) => prev - 1);
      } else if (isMovingForward && selectedImageIndex < imageUrls.length - 1) {
        setSelectedImageIndex((prev) => prev + 1);
      } else if (event.key === 'Escape') {
        onClose(event);
      }
    },
    [selectedImageIndex, isRtl, imageUrls.length, onClose],
  );

  const handleOnMouseDown = (event: React.MouseEvent) => {
    if (!imagesContainerRef.current) return;
    const e = event;
    const slider = imagesContainerRef.current;
    mouseCoords.current = {
      startX: e.pageX - slider.offsetLeft,
      startY: e.pageY - slider.offsetTop,
      scrollLeft: slider.scrollLeft,
      scrollTop: slider.scrollTop,
    };
    setIsMouseDown(true);
    document.body.style.cursor = 'grabbing';
  };

  const handleOnMouseUp = () => {
    setIsMouseDown(false);
    if (!imagesContainerRef.current) return;
    document.body.style.cursor = 'default';
  };

  const handleOnMouseMove = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isMouseDown || !imagesContainerRef.current) return;
    const slider = imagesContainerRef.current;
    const x = e.pageX - slider.offsetLeft;
    const y = e.pageY - slider.offsetTop;
    const walkX = (x - mouseCoords.current.startX) * MOUSE_EASE;
    const walkY = (y - mouseCoords.current.startY) * MOUSE_EASE;
    slider.scrollLeft = mouseCoords.current.scrollLeft - walkX;
    slider.scrollTop = mouseCoords.current.scrollTop - walkY;
  };

  useEffect(() => {
    if (isOpen && startImageIndex >= 0 && startImageIndex < imageUrls.length) {
      setSelectedImageIndex(startImageIndex);
    }
  }, [isOpen, startImageIndex]);

  useEffect(() => {
    const currentImage = imageRef.current[selectedImageIndex];
    if (currentImage !== null) {
      currentImage.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
      });
    }
  }, [selectedImageIndex]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  return (
    <div className={clsx(styles.galleryContainer, isOpen && styles.open)}>
      <button type='button' className={clsx(styles.buttonIcon, styles.close)} onClick={onClose}>
        <CloseIcon />
      </button>
      <div className={styles.overlay}></div>

      <div className={styles.imageAndCountContainer}>
        <Image
          key={imageUrls[selectedImageIndex]}
          className={styles.currentImage}
          noImagePlaceholderSrc={noImagePlaceholderSrc}
          src={getImageTransformationsUrl(imageUrls[selectedImageIndex], 692)}
        />

        <div className={styles.imageCount}>
          <div className={styles.activeIndex}>
            {translateNumber(selectedImageIndex + 1, locale === 'fa')}
          </div>
          &nbsp;/&nbsp;{translateNumber(imageUrls.length, locale === 'fa')}
        </div>

        {imageUrls.length > 1 ? (
          <>
            <button
              type='button'
              className={clsx(styles.buttonIcon, styles.left)}
              disabled={selectedImageIndex === 0}
              onClick={() => setSelectedImageIndex(selectedImageIndex - 1)}
            >
              <ChevronIcon />
            </button>

            <button
              type='button'
              className={clsx(styles.buttonIcon, styles.right)}
              disabled={selectedImageIndex === imageUrls.length - 1}
              onClick={() => setSelectedImageIndex(selectedImageIndex + 1)}
            >
              <ChevronIcon />
            </button>
          </>
        ) : null}
      </div>

      <div
        ref={imagesContainerRef}
        className={styles.imageThumbnailContainer}
        onMouseDown={handleOnMouseDown}
        onMouseUp={handleOnMouseUp}
        onMouseMove={handleOnMouseMove}
      >
        {imageUrls.map((imageUrl, index) => {
          return (
            <div
              key={imageUrl}
              onClick={() => {
                setSelectedImageIndex(index);
              }}
              ref={(el) => (imageRef.current[index] = el)}
              draggable={false}
            >
              <Image
                className={clsx(styles.image, index === selectedImageIndex && styles.selected)}
                noImagePlaceholderSrc={noImagePlaceholderSrc}
                src={getImageTransformationsUrl(imageUrl, 64)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ImageGallery;
